import { FC, useCallback } from "react";
import { ICellRendererParams } from "ag-grid-community";
import Stack from "@mui/material/Stack";
import { IconButton, Tooltip } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { useAppAbility } from "@/services/permissions";
import { Budget } from "@/services/cloudchipr.api";
import { BudgetIcon } from "@/components/pages/budgets-and-alerts/components/grids/budgets/components/BudgetIcon";
import { useAGGridRowHoverHook } from "@/components/pages/common/ag-grid/utils/hooks/useAGGridRowHover.hook";
import { useAppDispatch } from "@/store/hooks";
import { toggleAlertsOpenDrawer } from "@/store/alerts/alertsSlice";
import { initiateBudgetByIdThunk } from "@/store/budgets/thunks/initiateBudgetByIdThunk";

export const BudgetsNameCell: FC<ICellRendererParams<Budget>> = ({
  data: budget,
}) => {
  const dispatch = useAppDispatch();

  const id = budget?.id;
  const name = budget?.name;
  const isFilterBudget = !budget?.view && !!budget?.filter_tree;
  const { ref, hovered } = useAGGridRowHoverHook();

  const { can } = useAppAbility();
  const canEditBudget = can("edit", "budget");

  const clickHandler = useCallback(() => {
    if (!id) {
      return;
    }

    dispatch(toggleAlertsOpenDrawer());
    dispatch(initiateBudgetByIdThunk(id));
  }, [dispatch, id]);

  if (!id || !name) {
    return;
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" ref={ref}>
      <BudgetIcon base={isFilterBudget ? "filters" : "billingExplorerReport"} />

      {canEditBudget ? (
        <TypographyWithTooltip
          fontWeight="regular"
          color="primary"
          display="block"
          width="fit-content"
          sx={{ textDecoration: "underline", cursor: "pointer" }}
          variant="body2"
          title={name}
        />
      ) : (
        <TypographyWithTooltip maxWidth={200} variant="body2" title={name} />
      )}

      <Tooltip arrow placement="top" title="Edit Budget">
        <IconButton
          data-block-row-click
          size="small"
          sx={{
            ...iconButtonStyles,
            visibility: hovered ? "visible" : "hidden",
          }}
          disabled={!canEditBudget}
          onClick={clickHandler}
        >
          <EditOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
